import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, throwError, of, timer, forkJoin } from 'rxjs';
import { TickerInfo } from '../models/ticker';
import { switchMap, map, flatMap } from 'rxjs/operators';
import _mapKeys from 'lodash-es/mapKeys';
import _mapValues from 'lodash-es/mapValues';
import { PairInfo } from '../models/pair-info';
import { ProfileService } from './profile.service';
import { sortBy as _sortBy} from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class TickerService {

  tickerQuery = `
    data{
      price
      lastUpdate
      open24hour
      high24hour
      low24hour
      change24hour
      changePercentage24hour

      openPeriod
      lowHighPeriod
      changePeriod
    }
    usdData {
      price
      lastUpdate
      open24hour
      high24hour
      low24hour
      change24hour
      changePercentage24hour

      openPeriod
      lowHighPeriod
      changePeriod
    }
    pairInfo{
      pair
      pricePrecision
      maximumOrderSize{amount currency}
      minimumOrderSize{amount currency}
      isFavourite
    }
  `;

  constructor(private api: ApiService, private profileService: ProfileService) { }

  get(pairs: string[]): Observable<{[key: string]: TickerInfo}> {
    let query = '';

    pairs.forEach(pair => {
      query += `
        ${pair.replace('-', '_')}: tickers(pairs: ["${pair}"]) {
          ${this.tickerQuery}
        }
      `;
    });

    return this.api.get(`{
      ${query}
    }`, null, false).pipe(
      switchMap(response => {
        if (!response['data']) {
          return throwError(response);
        }

        const data = _mapKeys(response['data'], (val, key: string) => key.replace('_', '-'));
        return of(_mapValues(data, item => item ? item[0] : null));
      }),
      this.api.unifyErrors()
    );
  }

  getTickerInfo(pair: string): Observable<TickerInfo> {
    return this.api.get(`query tickers($pairs: [String!]){
      tickers(pairs: $pairs){
        ${this.tickerQuery}
      }
    }`, {
      pairs: [pair]
    }, false).pipe(
      switchMap(response => {
        const data = response['data'];
        if (!data || !data['tickers'] || !data['tickers'][0]) {
          return throwError(response);
        }

        return of(data['tickers'][0]);
      }),
      this.api.unifyErrors()
    );
  }

  getPairInfo(pair: string): Observable<PairInfo> {
    return this.api.get(`{
      tickers(pairs: ["${pair}"]){
        data{
          openPeriod
          lowHighPeriod
          changePeriod
        }
        pairInfo{
          pair
          pricePrecision
          maximumOrderSize{amount currency}
          minimumOrderSize{amount currency}
          isFavourite
        }
      }
    }`, null, false).pipe(
      switchMap(response => {
        const data = response['data'];
        if (!data || !data['tickers'] || !data['tickers'][0]) {
          return throwError(response);
        }

        if (data['tickers'][0].pairInfo && data['tickers'][0].data) {
          data['tickers'][0].pairInfo.openPeriod = data['tickers'][0].data.openPeriod;
          data['tickers'][0].pairInfo.lowHighPeriod = data['tickers'][0].data.lowHighPeriod;
          data['tickers'][0].pairInfo.changePeriod = data['tickers'][0].data.changePeriod;
        }

        return of(data['tickers'][0].pairInfo);
      }),
      this.api.unifyErrors()
    );
  }

  getAllData(): Observable<TickerInfo[]> {
    return this.api.get(`{
      tickers(allowedOnly: true) {
        ${this.tickerQuery}
      }
    }`).pipe(
      map(response => {
        if (!response['data']) {
          return throwError(response);
        }

        return response['data']['tickers'];
      }),
      this.api.unifyErrors()
    );
  }

  getAll(): Observable<{[key: string]: TickerInfo}> {
    return this.getAllData().pipe(
      map(response => {
        return _mapKeys(response, item => item && item.pairInfo ? item.pairInfo.pair : null);
      }),
    );
  }

  getAllSorted() {
    return forkJoin({
      tickers: this.getAllData(),
      preferences: this.profileService.getPreferences()
    }).pipe(
      map(response => {
        return _sortBy(response.tickers,
          (item: TickerInfo) => item && item.pairInfo && item.pairInfo.pair && item.pairInfo.pair.endsWith(response.preferences.fiatCurrencyCode) ? 0 : 1
        );
      })
    );
  }

  getAllPairs() {
    return this.getAllData().pipe(
      map(response => {
        return response.map(item => item && item.pairInfo ? item.pairInfo.pair : null);
      }),
    );
  }

  periodicPairInfo(pair: string, period: number): Observable<TickerInfo> {
    console.log('peeeriodic');
    return timer(0, period).pipe(
      flatMap(response => {
        console.log('peeeriodic 222');
        return this.getTickerInfo(pair);
      })
    );
  }

  setFavouritePairs(pairs: string[] = []): Observable<boolean> {
    return this.api.get(`mutation changeUserFavouritePairs($favouritePairs: [String!]!){
      changeUserFavouritePairs(favouritePairs: $favouritePairs) {
          ...on GeneralResponse{
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      favouritePairs: pairs
    }).pipe(
      switchMap(response => {
        const data = response['data']['changeUserFavouritePairs'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data.success);
      }),
      this.api.unifyErrors()
    );
  }
}
